import './FloatStepper.css';

import {
  forwardRef,
  ReactElement,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { IonIcon } from '@ionic/react';
import { chevronDownOutline, chevronUpOutline } from 'ionicons/icons';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import {
  LAYOUT_FLOAT_FIELD_STEP,
  LAYOUT_STEPPER_HOLD_DELAY,
  LAYOUT_STEPPER_HOLD_INTERVAL,
} from '../../../../../constants';
import { Control, GridInputValue } from '../../../../../types/types';

interface FloatStepperProps {
  value: GridInputValue;
  control?: Control;
  // Step size per click — 0.1 for float fields, 1 for integer ones.
  step?: number;
  onStep: (nextValue: number) => void;
}

// Imperative access for the input's ArrowUp/ArrowDown shortcut — a held key
// drives the same hold cadence as a held button.
export type FloatStepperHandle = {
  startHold: (direction: 1 | -1) => void;
  stopHold: () => void;
};

const FloatStepper = forwardRef<FloatStepperHandle, FloatStepperProps>(
  ({ value, control, step = LAYOUT_FLOAT_FIELD_STEP, onStep }, ref) => {
    // The auto-repeat interval outlives the render that armed it — reading the
    // prop through a ref keeps each tick stepping from the latest value.
    const valueRef = useRef(value);
    valueRef.current = value;
    const holdTimeoutRef = useRef<number | null>(null);
    const holdIntervalRef = useRef<number | null>(null);
    const { t } = useTranslate();

    const handleStep = (direction: 1 | -1): void => {
      const currentValue = valueRef.current;
      const parsedValue =
        typeof currentValue === 'number'
          ? currentValue
          : Number(currentValue ?? 0);
      const baseValue = Number.isNaN(parsedValue) ? 0 : parsedValue;
      let nextValue = Number((baseValue + direction * step).toFixed(2));

      const numberControl =
        control?.type === 'number' || control?.type === 'integer'
          ? control
          : undefined;

      if (numberControl?.min !== undefined) {
        nextValue = Math.max(numberControl.min, nextValue);
      }
      if (numberControl?.max !== undefined) {
        nextValue = Math.min(numberControl.max, nextValue);
      }

      onStep(nextValue);
    };

    const stopHold = (): void => {
      if (holdTimeoutRef.current !== null) {
        window.clearTimeout(holdTimeoutRef.current);
        holdTimeoutRef.current = null;
      }
      if (holdIntervalRef.current !== null) {
        window.clearInterval(holdIntervalRef.current);
        holdIntervalRef.current = null;
      }
    };

    // A press steps immediately; a held press starts auto-repeating after the
    // initial delay.
    const startHold = (direction: 1 | -1): void => {
      stopHold();
      handleStep(direction);

      holdTimeoutRef.current = window.setTimeout(() => {
        holdIntervalRef.current = window.setInterval(
          () => handleStep(direction),
          LAYOUT_STEPPER_HOLD_INTERVAL
        );
      }, LAYOUT_STEPPER_HOLD_DELAY);
    };

    useImperativeHandle(ref, () => ({ startHold, stopHold }));

    // A repeat must not survive the component (e.g. the panel re-renders away).
    useEffect(() => stopHold, []);

    const renderButton = (direction: 1 | -1): ReactElement => (
      <button
        type="button"
        className="float-stepper-button"
        // Mouse/touch only — keyboard steps via the input's arrow shortcut.
        tabIndex={-1}
        title={t(
          direction === 1 ? 'layoutEditor.stepUp' : 'layoutEditor.stepDown'
        )}
        onPointerDown={(): void => startHold(direction)}
        onPointerUp={stopHold}
        onPointerLeave={stopHold}
        onPointerCancel={stopHold}
      >
        <IonIcon
          icon={direction === 1 ? chevronUpOutline : chevronDownOutline}
        />
      </button>
    );

    return (
      <div className="float-stepper">
        {renderButton(1)}
        {renderButton(-1)}
      </div>
    );
  }
);

export default FloatStepper;
